/* Shared pieces of the v3 fleet console: toasts, the loading rule, empty and
   error states, the Zana mark and page links. Behaviour follows the design's
   "States" and "Errors and toasts" boards. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export type CSS = React.CSSProperties;

/* ── page addresses ───────────────────────────────────────────────────── */
export const PAGES = {
  ConsoleOverview: '/fleet',
  ConsoleLiveMap: '/fleet/map',
  ConsoleAlerts: '/fleet/alerts',
  ConsoleBuses: '/fleet/buses',
  ConsoleFuel: '/fleet/fuel',
  ConsoleMaintenance: '/fleet/maintenance',
  ConsoleTrips: '/fleet/trips',
  ConsolePeople: '/fleet/people',
  ConsoleReport: '/fleet/report',
  ConsoleAnalytics: '/fleet/analytics',
  ConsoleFuelPrices: '/fleet/fuel-prices',
  ConsoleSettings: '/fleet/settings',
  ConsoleBilling: '/fleet/billing',
  ConsoleZanaChat: '/fleet/zana',
} as const;
export type PageKey = keyof typeof PAGES;

/** Spread onto an <a>: real href (open in new tab works) + client-side nav. */
export function useGo() {
  const navigate = useNavigate();
  return useCallback((page: PageKey | string, search = '') => {
    const href = ((PAGES as Record<string, string>)[page] || page) + search;
    return {
      href,
      onClick: (e: React.MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(href);
      },
    };
  }, [navigate]);
}

/** Parse a design-style inline style string ("width: 380px; ...") into an object. */
export function css(s?: string | null): CSS {
  const out: Record<string, string> = {};
  if (!s) return out;
  s.split(';').forEach((d) => {
    const i = d.indexOf(':');
    if (i < 0) return;
    const k = d.slice(0, i).trim();
    const v = d.slice(i + 1).trim();
    if (!k) return;
    out[k.startsWith('--') ? k : k.replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = v;
  });
  return out as CSS;
}

/* ── icons (the design's 24px stroke set) ─────────────────────────────── */
export const Svg: React.FC<{ size?: number; children: React.ReactNode; style?: CSS }> = ({ size = 17, children, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none', ...style }}>
    {children}
  </svg>
);
export const IcX = ({ size = 16 }: { size?: number }) => <Svg size={size}><path d="M18 6 6 18M6 6l12 12" /></Svg>;
export const IcArrow = ({ size = 15 }: { size?: number }) => <Svg size={size}><path d="M5 12h14M13 6l6 6-6 6" /></Svg>;
export const IcSearch = ({ size = 17 }: { size?: number }) => <Svg size={size}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Svg>;
export const IcRetry = ({ size = 16 }: { size?: number }) => <Svg size={size}><path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" /><path d="M3 21v-5h5" /></Svg>;
export const IcAlert = ({ size = 16 }: { size?: number }) => <Svg size={size}><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.5" /></Svg>;
export const IcWifiOff = ({ size = 16 }: { size?: number }) => <Svg size={size}><path d="M2 2l20 20" /><path d="M8.5 16.5a5 5 0 0 1 7 0" /><path d="M5 12.9a10 10 0 0 1 5.2-2.7" /><path d="M19 12.9a10 10 0 0 0-2.1-1.6" /><path d="M12 20h.01" /></Svg>;
export const IcBus = ({ size = 17 }: { size?: number }) => <Svg size={size}><rect x="4" y="3" width="16" height="15" rx="3" /><path d="M4 11h16M8 18v2M16 18v2" /><circle cx="8" cy="14.5" r=".6" /><circle cx="16" cy="14.5" r=".6" /></Svg>;
export const IcUser = ({ size = 17 }: { size?: number }) => <Svg size={size}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Svg>;
export const IcPage = ({ size = 17 }: { size?: number }) => <Svg size={size}><path d="M14 3H6v18h12V7z" /><path d="M14 3v4h4" /></Svg>;

/** Zana's two-eye mark. Violet belongs to Zana alone. */
export const ZanaMark: React.FC<{ size?: 18 | 20 | 26 }> = ({ size = 18 }) => {
  const eye = size === 26 ? { w: 4, h: 6 } : size === 20 ? { w: 3, h: 5 } : { w: 3, h: 4 };
  return (
    <span aria-hidden="true" style={{ width: size, height: size, borderRadius: size === 26 ? 8 : 6, background: 'var(--z)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: size === 26 ? 4 : 3, flex: 'none' }}>
      <i style={{ width: eye.w, height: eye.h, borderRadius: 3, background: '#fff', display: 'block' }} />
      <i style={{ width: eye.w, height: eye.h, borderRadius: 3, background: '#fff', display: 'block' }} />
    </span>
  );
};

/* ── toasts ───────────────────────────────────────────────────────────────
   Bottom right, max 3, in 220 / out 160 ms, hover pauses, error stays until
   closed, warning 8 s, the rest 4 s. Progress toasts turn into a result. */
export type ToastKind = 'success' | 'error' | 'warning' | 'info' | 'zana' | 'progress';
export interface ToastIn { kind?: ToastKind; text: string; actLabel?: string; onAct?: () => void; ms?: number }
interface ToastItem extends ToastIn { id: string; kind: ToastKind; leaving?: boolean }
interface ToastApi {
  toast: (t: ToastIn) => string;
  update: (id: string, patch: Partial<ToastIn>) => void;
  dismiss: (id: string) => void;
}
const ToastCtx = createContext<ToastApi>({ toast: () => '', update: () => undefined, dismiss: () => undefined });
export const useToast = () => useContext(ToastCtx);

const T_ICON: Record<ToastKind, string> = { success: 'm5 12 5 5 9-10', error: 'M12 8v5M12 16.5v.5', warning: 'M12 8v5M12 16.5v.5', info: 'M12 11v6M12 7.5v.5', zana: 'M9 10v3M15 10v3', progress: '' };
const T_BG: Record<ToastKind, CSS> = {
  success: { background: '#1C7A4B', color: '#fff' }, error: { background: '#D64545', color: '#fff' },
  warning: { background: '#E8A33A', color: '#111520' }, info: { background: 'rgba(255,255,255,.16)', color: '#fff' },
  zana: { background: '#6F4FF0', color: '#fff' }, progress: {},
};
const defaultMs = (k: ToastKind) => (k === 'error' || k === 'progress' ? 0 : k === 'warning' ? 8000 : 4000);

export function useToastState() {
  const [list, setList] = useState<ToastItem[]>([]);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const dismiss = useCallback((id: string) => {
    clearTimeout(timers.current[id]);
    setList((l) => l.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
    setTimeout(() => setList((l) => l.filter((x) => x.id !== id)), 170);
  }, []);
  const arm = useCallback((id: string, ms: number) => {
    clearTimeout(timers.current[id]);
    if (ms) timers.current[id] = setTimeout(() => dismiss(id), ms);
  }, [dismiss]);
  const toast = useCallback((t: ToastIn) => {
    const id = String(Date.now()) + Math.random();
    const kind = t.kind || 'success';
    const ms = t.ms ?? defaultMs(kind);
    setList((l) => l.concat([{ ...t, id, kind, ms }]).slice(-3));
    if (ms && kind !== 'progress') arm(id, ms);
    return id;
  }, [arm]);
  const update = useCallback((id: string, patch: Partial<ToastIn>) => {
    setList((l) => l.map((x) => (x.id === id ? { ...x, ...patch, kind: patch.kind || x.kind, ms: patch.ms ?? (patch.kind ? defaultMs(patch.kind) : x.ms) } : x)));
    if (patch.kind && patch.kind !== 'progress' && patch.kind !== 'error') arm(id, patch.ms || 4000);
  }, [arm]);
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);
  const api = useMemo(() => ({ toast, update, dismiss }), [toast, update, dismiss]);
  return { list, api };
}
export const ToastProvider = ToastCtx.Provider;

export const ToastRegion: React.FC<{ list: ToastItem[]; dismiss: (id: string) => void; right: number }> = ({ list, dismiss, right }) => (
  <div className="tregion" style={{ right }} aria-live="polite">
    {list.map((t) => {
      const timed = !!t.ms && t.kind !== 'progress' && t.kind !== 'error';
      return (
        <div key={t.id} className={'tst' + (t.leaving ? ' out' : '')} role={t.kind === 'error' ? 'alert' : 'status'}>
          <span className="ti" style={T_BG[t.kind] || T_BG.info}>
            {t.kind === 'progress' ? <span className="spin" /> : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={T_ICON[t.kind] || T_ICON.info} /></svg>
            )}
          </span>
          <span className="tt">{t.text}</span>
          {t.actLabel && (
            <button type="button" className="ta" onClick={() => { if (t.onAct) t.onAct(); dismiss(t.id); }}>{t.actLabel}</button>
          )}
          <button type="button" className="tx" aria-label="Dismiss" onClick={() => dismiss(t.id)}><IcX /></button>
          {timed && <span className="tbar" style={{ animationDuration: `${t.ms || 4000}ms` }} />}
        </div>
      );
    })}
  </div>
);

/** Run an async action behind a progress toast that becomes success or error. */
export function useAction() {
  const { toast, update } = useToast();
  return useCallback(async <T,>(o: { busy: string; done: string | ((r: T) => string); fail: string; run: () => Promise<T>; actLabel?: string; onAct?: () => void; retry?: boolean }) => {
    const id = toast({ kind: 'progress', text: o.busy });
    try {
      const r = await o.run();
      update(id, { kind: 'success', text: typeof o.done === 'function' ? o.done(r) : o.done, actLabel: o.actLabel, onAct: o.onAct });
      return r;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[console]', o.fail, e);
      update(id, { kind: 'error', text: o.fail });
      return undefined;
    }
  }, [toast, update]);
}

/* ── the loading rule ─────────────────────────────────────────────────────
   Skeleton in the page's own layout, shown only after 150 ms (never flashed),
   and once shown it stays at least 400 ms. */
export function useSkeleton(loading: boolean): boolean {
  const [show, setShow] = useState(false);
  const shownAt = useRef(0);
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    if (loading) {
      t = setTimeout(() => { shownAt.current = Date.now(); setShow(true); }, 150);
    } else if (show) {
      const left = Math.max(0, 400 - (Date.now() - shownAt.current));
      t = setTimeout(() => setShow(false), left);
    }
    return () => clearTimeout(t);
  }, [loading, show]);
  return show;
}

/** Page wrapper: skeleton while loading (by the rule above), content rising in after. */
export const PageBody: React.FC<{ loading: boolean; skeleton: React.ReactNode; children: React.ReactNode }> = ({ loading, skeleton, children }) => {
  const sk = useSkeleton(loading);
  if (sk) return <>{skeleton}</>;
  if (loading) return null;
  return <div className="up" style={{ height: '100%' }}>{children}</div>;
};

/* skeleton building blocks (match the design's shim rows) */
export const Shim: React.FC<{ w: string | number; h: string | number; style?: CSS }> = ({ w, h, style }) => <div className="shim" style={{ width: w, height: h, ...style }} />;
export const ShimRow: React.FC = () => (
  <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '16px 20px', borderTop: '1px solid var(--line)' }}>
    <Shim w={32} h={32} style={{ borderRadius: '50%' }} />
    <Shim w="22%" h={14} /><Shim w="14%" h={14} /><Shim w="10%" h={22} style={{ borderRadius: 11 }} />
    <div style={{ flex: 1 }} /><Shim w="8%" h={14} />
  </div>
);
export const ShimHead: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}><Shim w={280} h={30} /><Shim w={420} h={16} /></div>
);
export const ShimTable: React.FC<{ rows?: number }> = ({ rows = 7 }) => (
  <div className="panel">
    <div className="shim" style={{ width: '100%', height: 52, borderRadius: '12px 12px 0 0', background: 'var(--s1)', borderBottom: '1px solid var(--line)' }} />
    {Array.from({ length: rows }).map((_, i) => <ShimRow key={i} />)}
  </div>
);
export const ShimStats: React.FC = () => (
  <div className="stats">
    {[0, 1, 2, 3].map((i) => (
      <div className="stat" key={i}><Shim w="40%" h={12} /><Shim w="60%" h={30} style={{ marginTop: 6 }} /><Shim w="50%" h={12} style={{ marginTop: 4 }} /></div>
    ))}
  </div>
);
export const PageSkeleton: React.FC<{ stats?: boolean; rows?: number }> = ({ stats, rows }) => (
  <div className="wrap"><ShimHead />{stats && <ShimStats />}<ShimTable rows={rows} /></div>
);

/* ── empty, no results, couldn't load ─────────────────────────────────── */
export const Empty: React.FC<{ icon?: React.ReactNode; title: string; body?: React.ReactNode; children?: React.ReactNode }> = ({ icon, title, body, children }) => (
  <div className="empty">
    <span className="eic">{icon || <IcPage size={22} />}</span>
    <h3 className="h3">{title}</h3>
    {body && <p className="sub" style={{ maxWidth: 420 }}>{body}</p>}
    {children && <div className="row" style={{ gap: 8, marginTop: 4, flexWrap: 'wrap', justifyContent: 'center' }}>{children}</div>}
  </div>
);

/** "No bus matches “kdx 991”" — says what was searched and how to fix it. */
export const NoResults: React.FC<{ what: string; q: string; hint: string; onClear: () => void }> = ({ what, q, hint, onClear }) => (
  <Empty icon={<IcSearch size={22} />} title={`No ${what} matches “${q}”`} body={hint}>
    <button type="button" className="btn s b-line" onClick={onClear}>Clear search</button>
  </Empty>
);

/** Errors keep the last good data on screen, dimmed, and say when it is from. */
export const StaleBanner: React.FC<{ what: string; since: Date | null; onRetry?: () => void }> = ({ what, since, onRetry }) => (
  <div className="banner warn" role="status">
    <span style={{ color: 'var(--warn)' }}><IcAlert /></span>
    <span style={{ flex: 1 }}>
      <b>Couldn’t refresh {what}.</b>{' '}
      {since ? <>You are seeing the {what} from {hhmm(since)}. We will keep trying every 30 seconds.</> : <>We will keep trying every 30 seconds.</>}
    </span>
    {onRetry && <button type="button" className="btn s b-line" onClick={onRetry}><IcRetry size={15} />Try again</button>}
  </div>
);

/** "You’re offline. Changes are saved and will send when you reconnect." */
export function useOnline() {
  const [on, setOn] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const a = () => setOn(true); const b = () => setOn(false);
    window.addEventListener('online', a); window.addEventListener('offline', b);
    return () => { window.removeEventListener('online', a); window.removeEventListener('offline', b); };
  }, []);
  return on;
}
export const OfflineBanner: React.FC = () => (
  <div className="banner mute" role="status">
    <span style={{ color: 'var(--muted)' }}><IcWifiOff /></span>
    <span>You’re offline. Changes are saved and will send when you reconnect.</span>
  </div>
);

/* ── formatting ───────────────────────────────────────────────────────── */
export const hhmm = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
export const dayMonth = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
export const fullDate = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
export const n0 = (n: number) => Math.round(n).toLocaleString('en-GB');
export const ksh = (n: number) => 'Ksh ' + n0(n);
export const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '·';
export function ago(d: Date | null): string {
  if (!d) return '—';
  const today = new Date();
  const same = d.toDateString() === today.toDateString();
  if (same) return `${hhmm(d)} today`;
  const y = new Date(today); y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return `Yesterday ${hhmm(d)}`;
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}
/** "In 12 days" / "2 days overdue" / "Today". */
export function dueIn(d: Date): { text: string; days: number } {
  const ms = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - new Date(new Date().toDateString()).getTime();
  const days = Math.round(ms / 86400000);
  if (days === 0) return { text: 'Today', days };
  if (days < 0) return { text: `${-days} day${days === -1 ? '' : 's'} overdue`, days };
  return { text: `In ${days} day${days === 1 ? '' : 's'}`, days };
}
export const plural = (n: number, one: string, many = one + 's') => `${n0(n)} ${n === 1 ? one : many}`;

const plateSquash = (s: string) => s.replace(/[^a-z0-9]/gi, '').toUpperCase();
/** The plate to print beside a bus's name, or null when the name already holds it
    ("Bus KCE998Z" with plate KCE998Z), so no list shows the plate twice. */
export function plateBeside(b: { name: string; plate: string }): string | null {
  if (!b.plate) return null;
  return plateSquash(b.name).includes(plateSquash(b.plate)) ? null : b.plate;
}
/** "Donholm (sim) · KDJ219P", or "Bus KCE998Z" alone. */
export function busWithPlate(b: { name: string; plate: string }): string {
  const p = plateBeside(b);
  return p ? `${b.name} · ${p}` : b.name;
}

/** Count up 400 ms (the design's "Numbers" motion); jumps when reduced motion is on. */
export function useCountUp(target: number, ms = 400) {
  const [v, setV] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce || from.current === target) { setV(target); from.current = target; return; }
    const start = performance.now(); const a = from.current;
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / ms);
      setV(a + (target - a) * (1 - Math.pow(1 - k, 3)));
      if (k < 1) raf = requestAnimationFrame(step); else from.current = target;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

/* ── status chip and sparkline ────────────────────────────────────────── */
export type ChipTone = 'teal' | 'warn' | 'ok' | 'mute' | 'bad' | 'z';
const STATUS_TONE: Record<string, ChipTone> = { on_route: 'teal', late: 'warn', at_school: 'ok', idle: 'mute', off_road: 'bad' };
export const Chip: React.FC<{ tone: ChipTone; dot?: boolean; small?: boolean; children: React.ReactNode }> = ({ tone, dot, small, children }) => (
  <span className={`chip c-${tone}`} style={small ? { height: 22, fontSize: 12 } : undefined}>{dot && <span className="d" />}{children}</span>
);
export const StatusChip: React.FC<{ status: string; label: string }> = ({ status, label }) => (
  <Chip tone={STATUS_TONE[status] || 'mute'} dot>{label}</Chip>
);
/** The design's 7-point trend: 96×28, stroke in the change's colour. */
export const Spark: React.FC<{ values: number[]; tone: 'ok' | 'bad' | 'muted' }> = ({ values, tone }) => {
  if (values.length < 2) return null;
  const min = Math.min(...values); const max = Math.max(...values); const span = max - min || 1;
  const pts = values.map((v, i) => `${(2 + (92 * i) / (values.length - 1)).toFixed(1)},${(25 - (22 * (v - min)) / span).toFixed(1)}`).join(' ');
  return (
    <svg width="96" height="28" viewBox="0 0 96 28" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={`var(--${tone})`} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};
/** "Zana noticed" line: one per page at most, in the header; never opens the panel by itself. */
export const ZanaNote: React.FC<{ children: React.ReactNode; onAsk: () => void; label?: string }> = ({ children, onAsk, label = 'Ask Zana why' }) => (
  <div className="znote">
    <ZanaMark />
    <span>{children}</span>
    <button type="button" onClick={onAsk}>{label}<IcArrow size={14} /></button>
  </div>
);
